import {
  type GetStaticPaths,
  type InferGetServerSidePropsType,
  type NextPage,
} from "next";
import Link from "next/link";
import React from "react";
import CategoryComponents from "~/components/CategoryComponents";
import CategoryTitle from "~/components/CategoryTitle";
import GridLoop from "~/components/GridLoop";
import Layout from "~/components/Layout";
import Pagination, { usePagination } from "~/components/Pagination";
import PodcastLayout from "~/components/multimedia/PodcastLayout";
import { getCategoriesSlug } from "~/lib/db/category";
import { getMultimediaElemets } from "~/lib/db/multimedia";
import { ISSUES_TYPE, type ArticlePreviewType } from "~/types";

const DEFAULT_ITEMS_PER_PAGE = 18;

const AudioTerms: NextPage<
  InferGetServerSidePropsType<typeof getStaticProps>
> = ({
  category_slug,
  parent_category_slug,
  parent_category_name,
  parent_category_description,
  articles,
  subCategory,
}) => {
  const articlessShow = usePagination(articles, DEFAULT_ITEMS_PER_PAGE);
  const displaySubcategories = subCategory.map((el, index) => {
    return {
      id: index,
      item: {
        term: el,
      },
    };
  });
  if (parent_category_slug == "podcast") {
    return (
      <Layout title="Multimedia">
        <PodcastLayout
          categorySlug={category_slug}
          podcastArticles={articles}
          subCategory={subCategory}
          parentCategoryName={parent_category_name}
          parentCategorySlug={parent_category_slug}
        />
      </Layout>
    );
  }
  return (
    <Layout title="Multimedia">
      <section
        className={`${subCategory.length > 0 ? "bg-green" : ""} -mt-12 w-full`}
      >
        <div className="mx-auto max-w-box px-4 pb-32 pt-24">
          <h3 className="mb-4 text-lg font-bold">
            <Link href={`/audio-bs`} className="font-bold">
              Audio & podcast
            </Link>
          </h3>

          <div className="mb-20 lg:mb-32">
            <h1
              className={`${
                subCategory.length > 0 && "text-white"
              } text-4xl font-light md:text-6xl`}
            >
              {parent_category_name}
            </h1>
            {parent_category_description && (
              <p
                className={`${
                  subCategory.length > 0 &&
                  !parent_category_slug.includes("lo-studio") &&
                  "text-white"
                } mb-10 mt-10 text-lg font-medium md:mb-24`}
              >
                {parent_category_description}
              </p>
            )}
          </div>
          {subCategory.length > 0 ? (
            <div className="grid items-start gap-24 md:grid-cols-2 lg:grid-cols-3">
              {displaySubcategories.map((item, index) => (
                <CategoryComponents
                  key={index}
                  data={item}
                  tax={ISSUES_TYPE.multimedia}
                />
              ))}
            </div>
          ) : (
            <GridLoop
              gridloop={articlessShow as ArticlePreviewType[]}
              categoria={"category"}
              imageShow={true}
            />
          )}
        </div>
      </section>
      <div
        className={`mx-auto mt-6 max-w-box px-4 text-center ${
          (subCategory.length > 0 && !parent_category_slug.includes("lo-studio")
            ? displaySubcategories.length
            : articles.length) > DEFAULT_ITEMS_PER_PAGE
            ? ""
            : "hidden"
        }`}
      >
        <Pagination
          totalResults={articles.length}
          itemsPerPage={DEFAULT_ITEMS_PER_PAGE}
        />
      </div>
    </Layout>
  );
};

export default AudioTerms;

export const getStaticPaths: GetStaticPaths<{
  multimedia: string;
}> = async () => {
  const categoriesSlug = await getCategoriesSlug(ISSUES_TYPE.multimedia);

  return {
    // podcast slug already used by podcast.tsx
    paths: categoriesSlug
      .filter((category) => category.slug !== "podcast")
      .map((category) => ({
        params: { multimedia: category.slug },
      })),
    fallback: "blocking", //indicates the type of fallback
  };
};

export const getStaticProps = async ({ params }) => {
  if (!params) return { notFound: true };

  const multimedia = await getMultimediaElemets(params.multimedia as string);

  if (!multimedia) return { notFound: true };

  return {
    props: {
      category_slug: multimedia.category_slug,
      parent_category_slug: multimedia.parent_category_slug,
      parent_category_name: multimedia.parent_category_name,
      parent_category_description:
        multimedia.parent_category_description ?? null,
      subCategory: multimedia.subCategory,
      articles: multimedia.articles,
    },
    //revalidate: 10 * 60,
  };
};
